import { z } from "zod";

// Zod schemas for Opportunities APIs
export const FilterResponseSchema = z.object({
  label: z.string(),
  value: z.string(),
});

// coercing to string as explorer opportunities sends money savings as string, but other apis send it as number
export const MoneySavingsSchema = z.object({
  min: z.coerce.string(),
  max: z.coerce.string(),
});

export const ResourceSchema = z.object({
  resource_name: z.string(),
  resource_uri: z.string(),
  resource_type: z.number(),
  resource_id: z.string().optional(),
});

export const SavingsAttributionSchema = z.object({
  id: z.string().optional(),
  opportunity_id: z.string().optional(),
  // The backend serializes the ORM int; newer builds send a string. Normalize to string.
  instance_id: z.coerce.string().nullable().optional(),
  owner: z.number().optional(),
  attribution_type: z.string().optional(),
  altimate_estimated_cost_saving: z.number().nullable().optional(),
  altimate_estimated_time_saving: z.number().nullable().optional(),
  user_estimated_cost_saving: z.number().nullable().optional(),
  user_estimated_time_saving: z.number().nullable().optional(),
  user_savings_updated_by: z.number().nullable().optional(),
  savings_metadata: z.unknown().nullable().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.number().optional(),
  last_modified_by: z.number().optional(),
});

export const GetOpportunitiesDataResponseSchema = z.object({
  opportunity_id: z.string(),
  resource_name: z.string(),
  resource_type: z.coerce.number(),
  opportunity_name: z.string(),
  description: z.string().nullable().optional(),
  last_seen: z.string(),
  first_seen: z.string(),
  count: z.number(),
  total_count: z.number().nullable().optional(),
  resources: z.array(ResourceSchema),
  money_savings: MoneySavingsSchema,
  time_savings: z.string(),
  raw_time_savings: MoneySavingsSchema.optional(),
  effort: z.number(),
  owner: z.coerce.string().nullable().optional(),
  status: z.number(),
  linenumber: z.string().optional(),
  explanation_context: z.unknown().optional(),
  savings_attribution: SavingsAttributionSchema.optional().nullable(),
  metadata: z
    .object({
      report: z.object({ actual_query_span: z.string(), type: z.string() }).optional(),
      // Pre-formatted, agent-ready markdown produced by the backend. Rendered
      // directly in the Agent Instructions tab. May be absent until the BE
      // populates it for a given opportunity.
      agent_instructions: z.string().nullish(),
    })
    .optional(),
  instance_id: z.coerce.number(),
  resource_uri: z.string().optional(),
  // Backend-resolved deep-link path for this opportunity's primary resource.
  // Null for priority/user opportunities (routed to the intermediary detail
  // page) and for rows whose resource can't be resolved server-side; the FE
  // then falls back to the /opportunities/redirection API or the detail page.
  redirect_url: z.string().nullable().optional(),
  // State transition the recommendation applies (e.g. warehouse size "M" →
  // "S", cluster node count "4" → "3"). Absent or '' on rows written before
  // this field existed, and on fixed-target rules that never populate it —
  // the FE renders nothing in that case rather than a placeholder.
  from_state: z.string().nullable().optional(),
  target_state: z.string().nullable().optional(),
  // Server-computed era identity (opportunity_id + target_state, single
  // source of truth). Absent on an older backend that hasn't deployed this
  // field yet — the FE falls back to assembling the same key locally
  // (see `getEraKey`) in that window.
  era_key: z.string().nullable().optional(),
  // Money saved by an implemented opportunity; null unless it has an
  // attribution. Resource and detail reads return the total since
  // implementation, never clipped to the request's range.
  saved_money: z.number().nullable().optional(),
});

export const PriorityOpportunityResponseSchema = GetOpportunitiesDataResponseSchema.extend({
  priority_rank: z.number().nullable().optional(),
  priority_se_notes: z.string().nullable().optional(),
});

// The Implemented tab reads from the dedicated /opportunities/implemented
// endpoint. money_savings / time_savings are re-derived server-side for the
// selected window (so they reconcile with the Summary tile) and reuse the
// shared columns unchanged. The only extra field is the implementation date,
// shown in its own column (savings are attributed for 365 days from it). It
// lives on the specific extender (not the shared base) so an implemented-only
// field can't leak into the regular Opportunities response, and is
// optional/nullable: the backend omits it for an implemented opportunity with
// no attribution row, and it is absent entirely on an older backend — so it
// still parses cleanly.
export const GetImplementedOpportunitiesDataResponseSchema =
  GetOpportunitiesDataResponseSchema.extend({
    attribution_start_date: z.string().nullable().optional(),
  });

export const CommentSchema = z.object({
  id: z.string(),
  opportunity_id: z.string(),
  instance_id: z.number().nullable().optional(),
  comment: z.string(),
  created_by: z.number().nullable().optional(),
  timestamp: z.string(),
  updated_at: z.string().nullable().optional(),
  edited: z.boolean().default(false),
});

export const OpportunityDetailResponseSchema = GetOpportunitiesDataResponseSchema.extend({
  labels: z.array(z.string()).default([]),
  is_prioritized: z.boolean().default(false),
  // Origin type from metadata label: "user" (Studio-created), "expert"
  // (SE-curated priority), null (system/pipeline-detected).
  opportunity_type: z.string().nullable().optional(),
  priority_se_notes: z.string().nullable().optional(),
  comments_count: z.number().default(0),
  comments: z.array(CommentSchema).default([]),
});

export const LabelSuggestionResponseSchema = z.object({
  items: z.array(z.string()),
});

export const GetGroupedOpportunitiesDataResponseSchema = z.object({
  opportunity_name: z.string(),
  resource_type: z.number(),
  description: z.string().nullable().optional(),
  cost_savings_max: z.number(),
  cost_savings_min: z.number(),
  // Seconds are used for sorting and as a display fallback for older API responses.
  time_savings_max: z.number(),
  time_savings_min: z.number(),
  time_savings_max_formatted: z.string().nullish(),
  time_savings_min_formatted: z.string().nullish(),
  occurrences: z.number(),
});

// Value can be a string (for most labels) or an object with min/max (for Money Savings)
const SummaryValueSchema = z.union([
  z.string(),
  z.object({ min: z.coerce.string(), max: z.coerce.string() }),
]);

export const GetOpportunitiesSummaryResponseSchema = z.object({
  label: z.string(),
  value: SummaryValueSchema,
});

export const GetOpportunitiesResourceResponseSchema = z.object({
  data: z.array(GetOpportunitiesDataResponseSchema),
});

export const GetOpportunitiesRedirectResponseSchema = z.object({
  url: z.string().nullable(),
});

export const GetOpportunityUpdatesResponseSchema = z.object({
  opportunity_id: z.string(),
  timestamp: z.string(),
  owner: z.string().nullable(),
  status: z.string().nullable(),
  created_by: z.string().nullable(),
});

export const GetScheduledEventUpdatesResponseSchema = z.object({
  event_id: z.string(),
  event_type: z.string(),
  comment: z.string(),
  created_at: z.string(),
  created_by: z.string(),
});

// Reports vary by analyzer; a global report does not carry SQL-match fields.
// The backend passes stored analyzer reports through untyped (`tags: List[dict]`),
// so an absent field can arrive as either missing or null (FE-16W, FE-16X).
export const QueryLineageTagReportSchema = z.object({
  actual_query_span: z.string().nullish(),
  description: z.string().nullish(),
  found_index_match: z.boolean().nullish(),
  functions: z.unknown(),
  indices: z.array(z.unknown()).nullish(),
  metadata: z
    .object({
      attributes: z.unknown(),
      functions: z.unknown(),
      query_span: z.string().nullish(),
    })
    .nullish(),
  query_span: z.string().nullish(),
  type: z.string().nullish(),
});

const PercentValueSchema = z.object({
  percent: z.string().optional(),
  value: z.string(),
  tooltip: z.string(),
});

export const QueryLineageTagSchema = GetOpportunitiesDataResponseSchema.omit({
  metadata: true,
}).extend({
  // Lineage has resources[], not necessarily Discover's primary-resource fields.
  resource_name: z.string().optional(),
  resource_type: GetOpportunitiesDataResponseSchema.shape.resource_type.optional(),
  opportunity_timestamp: z.string().optional(),
  cost_savings_min: z.number().optional(),
  cost_savings_max: z.number().optional(),
  time_savings_min: z.number().optional(),
  time_savings_max: z.number().optional(),
  cost_savings_ui: PercentValueSchema.optional(),
  time_savings_ui: PercentValueSchema.optional(),
  resources_name: z.array(z.string()).optional(),
  resources_id: z.array(z.string()).optional(),
  resources_uri: z.array(z.string()).optional(),
  resources_type: z.array(z.number()).optional(),
  occurrences: z.number().optional(),
  tag_rk: z.string().optional(),
  reason: z.string().optional(),
  metadata: z
    .object({
      report: QueryLineageTagReportSchema,
      agent_instructions: z.string().nullish(),
    })
    .optional(),
  id: z.string().optional(),
  parent_indices: z
    .tuple([z.tuple([z.number(), z.number()]), z.tuple([z.number(), z.number()])])
    .optional(),
  markdown_description: z.string().nullish(),
});

// Nodes contain either the same formatted opportunity as tags[] or a lightweight
// legacy tag. Normalize only string/number savings for existing display helpers.
const NodeSavingsSchema = z.union([z.string(), z.number().finite()]).transform(String);
export const QueryTagMetaSchema = QueryLineageTagSchema.partial()
  .extend({
    tag_rk: z.string(),
    reports: z
      .array(z.object({ savings: z.string().optional(), performance: z.string().optional() }))
      .optional(),
    cost_savings_min: NodeSavingsSchema.optional(),
    cost_savings_max: NodeSavingsSchema.optional(),
  })
  .passthrough();

export const GetQuerySQLLineageResponseSchema = z.object({
  tableEdges: z.array(z.tuple([z.string(), z.string()])),
  details: z.record(z.unknown()),
  nodePositions: z.record(z.tuple([z.number(), z.number()])).nullable(),
  query_tags_meta: z.record(z.array(QueryTagMetaSchema)),
  tags: z.array(QueryLineageTagSchema),
  is_lineage: z.boolean().optional(),
  is_error: z.boolean().optional(),
});

export const OpportunitiesFiltersSchema = z.object({
  page: z.number().optional(),
  size: z.number().optional(),
  start_date: z.string(),
  end_date: z.string().optional(),
  period: z.string().optional(),
  sort: z.string().optional(),
  sort_order: z.string().optional(),
  effort: z.array(z.string()).optional(),
  resource_name: z.array(z.string()).optional(),
  opportunity_name: z.array(z.string()).optional(),
  resource_type: z.array(z.string()).optional(),
  owner: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  user: z.array(z.string()).optional(),
  warehouse: z.array(z.string()).optional(),
  instance_id: z.array(z.string()).optional(),
  // Databricks workspace filter set by the More-Filters Workspace chip.
  // Reads from `?workspaceId=` URL params (see useDiscoverFilters) and
  // is sent to the BE's GetOpportunitiesDataRequest.workspace_id field.
  // Ignored on non-Databricks tenants.
  workspace_id: z.array(z.string()).optional(),
});

export type OpportunitiesFilters = z.infer<typeof OpportunitiesFiltersSchema>;

export const GroupedOpportunitiesFiltersSchema = z.object({
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  effort: z.array(z.string()).optional(),
  resource_name: z.array(z.string()).optional(),
  opportunity_name: z.array(z.string()).optional(),
  resource_type: z.array(z.string()).optional(),
  owner: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  user: z.array(z.string()).optional(),
  warehouse: z.array(z.string()).optional(),
  instance_id: z.array(z.string()).optional(),
  workspace_id: z.array(z.string()).optional(),
});

export type GroupedOpportunitiesFilters = z.infer<typeof GroupedOpportunitiesFiltersSchema>;

// Re-export types from schemas
export type GetOpportunityUpdatesResponse = z.infer<typeof GetOpportunityUpdatesResponseSchema>;
export type GetScheduledEventUpdatesResponse = z.infer<
  typeof GetScheduledEventUpdatesResponseSchema
>;
